import { Link } from "@tanstack/react-router";
import type { OnboardingRecordDto } from "../lib/fetch-transporter-onboarding";
import { TRANSPORTER_ONBOARDING_FLOW, type TransporterOnboardingStep } from "../lib/fetch-transporter-onboarding";
import { useTransporterWorkspace } from "./transporter-workspace-context";

const STEP_LABEL: Record<TransporterOnboardingStep, string> = {
  kyb_verification: "Business verification (KYB)",
  bank_account_linking: "Bank & payouts",
  fee_schedule_agreement: "Fee schedule",
  vehicle_registration: "Fleet vehicles",
  driver_registration: "Drivers & credentials",
  completed: "Finish setup",
};

function formatIso(iso: string | null | undefined): string {
  if (iso == null || iso === "") return "—";
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

function recordAt(records: OnboardingRecordDto[] | null, step: string): string | null {
  return records?.find((r) => r.step === step)?.completedAt ?? null;
}

/** Organization tab: full step list with completion times after onboarding is finished. */
export function TransporterOnboardingOrgReadonlySummary() {
  const { workspaceProfile, onboardingRecords } = useTransporterWorkspace();
  const doneAt = workspaceProfile?.onboardingCompletedAt;
  if (!doneAt) return null;

  return (
    <section className="rounded-2xl border border-td-green/20 bg-td-green-light p-6 text-sm shadow-td-card">
      <h2 className="font-medium text-td-green">Onboarding</h2>
      <p className="mt-1 text-xs text-td-muted">
        Completed <span className="font-mono text-td-muted">{formatIso(doneAt)}</span>. Update organization, compliance,
        and payout details in the sections below.
      </p>
      <dl className="mt-3 grid gap-2 text-td-muted">
        {TRANSPORTER_ONBOARDING_FLOW.filter((s) => s !== "completed").map((step) => (
          <div
            key={step}
            className="flex flex-wrap justify-between gap-2 border-t border-td-card-border pt-2 first:border-t-0 first:pt-0"
          >
            <dt>{STEP_LABEL[step]}</dt>
            <dd className="font-mono text-xs">{formatIso(recordAt(onboardingRecords, step))}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/** Earnings: fee schedule + bank readiness context (read-only). */
export function TransporterOnboardingEarningsReadonlySummary() {
  const { workspaceProfile, onboardingRecords } = useTransporterWorkspace();
  if (!workspaceProfile?.onboardingCompletedAt) return null;
  const fee = recordAt(onboardingRecords, "fee_schedule_agreement");
  const bank = recordAt(onboardingRecords, "bank_account_linking");
  const last4 = workspaceProfile.bankAccountLast4?.trim();
  const bankName = workspaceProfile.bankName?.trim();
  return (
    <div className="td-surface p-6 text-sm">
      <p className="font-medium text-td-ink">Payouts & fees</p>
      <p className="mt-1 text-td-muted">
        Fee schedule accepted <span className="font-mono text-xs">{formatIso(fee)}</span>
        {" · "}
        Bank step completed <span className="font-mono text-xs">{formatIso(bank)}</span>
      </p>
      <p className="mt-2 text-xs text-td-muted">
        Linked account
        {bankName ? (
          <>
            : <span>{bankName}</span>
            {last4 ? (
              <span className="font-mono">
                {" "}
                ·•••{last4}
              </span>
            ) : null}
          </>
        ) : (
          " —"
        )}
        .{" "}
        <Link to="/workspace/account" search={{ tab: "payout" }} className="text-blue underline hover:text-blue">
          Edit in Account
        </Link>
      </p>
    </div>
  );
}
